import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import { param, rankTier } from '../../common/community';
const SHOWN = 10;
const millions = v => v ? (v / 1e6).toFixed(1).replace(/\.0$/, '') + 'M' : '—';
const naira = (v, unit) => v === null || v === undefined ? '—' : '₦' + (unit === 'trn' ? Number(v).toFixed(2) : Number(v).toFixed(1).replace(/\.0$/, '')) + unit;

/**
 * /community/kingdoms: laid out like the governors page. States in the governors ranking's order:
 * the top state as a featured card, the rest as rows, each with population, IGR, GDP, its kingdoms
 * and their average age. Every state starts collapsed; opening one lists its kingdoms.
 * ?state=lagos (from a governor card) shows that state, opened.
 */
export default class CommunityKingdoms extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.states = null;
    this.query = '';
    this.chosen = param('state') || null;
    this.open = this.chosen;
    this.showAll = false;
    const api = app.forum.attribute('apiUrl');
    Promise.all([app.request({
      method: 'GET',
      url: api + '/datahub/rulers',
      errorHandler: () => {}
    }).catch(() => ({
      rulers: []
    })), app.request({
      method: 'GET',
      url: api + '/voucha-community/governors',
      errorHandler: () => {}
    }).catch(() => ({
      governors: []
    }))]).then(([r, g]) => {
      this.states = this.build(r && r.rulers || [], g && g.governors || []);
      this.ranked = (g && g.governors || []).filter(x => x.rank).length;
      m.redraw();
    });
  }

  /** One entry per state: the governors data (rank, figures) and its kingdoms */
  build(rulers, governors) {
    const by = {};
    governors.forEach(g => {
      if (g.slug) by[g.slug] = {
        slug: g.slug,
        state: g.state,
        zone: g.zone,
        rank: g.rank || null,
        population: g.population || null,
        igr: g.igr || null,
        gdp: g.gdp || null,
        kingdoms: []
      };
    });
    rulers.forEach(k => {
      const key = k.slug || k.state;
      if (!by[key]) by[key] = {
        slug: key,
        state: k.state || 'Other',
        zone: k.zone,
        rank: null,
        population: null,
        igr: null,
        gdp: null,
        kingdoms: []
      };
      by[key].kingdoms.push(k);
    });
    return Object.values(by).filter(s => s.kingdoms.length).map(s => {
      const ages = s.kingdoms.map(k => k.years).filter(y => y && y > 0);
      s.avgAge = ages.length ? Math.round(ages.reduce((a, b) => a + b, 0) / ages.length) : null;
      return s;
    }).sort((a, b) => (a.rank || 999) - (b.rank || 999) || a.state.localeCompare(b.state));
  }
  toggle(s) {
    this.open = this.open === s.slug ? null : s.slug;
  }
  showState(slug) {
    this.chosen = slug;
    this.open = slug;
    this.showAll = false;
    try {
      window.history.replaceState(window.history.state, '', '/community/kingdoms' + (slug ? '?state=' + encodeURIComponent(slug) : ''));
    } catch (e) {}
  }
  stat(icon, value, label) {
    return m("div", {
      className: "VGov-stat"
    }, m("i", {
      className: icon,
      "aria-hidden": "true"
    }), m("span", null, m("strong", null, value), m("small", null, label)));
  }
  stats(s, full) {
    return [this.stat('fas fa-users', millions(s.population), 'Population'), this.stat('fas fa-coins', s.igr ? s.igr.value >= 1000 ? '₦' + (s.igr.value / 1000).toFixed(2) + 'trn' : '₦' + Math.round(s.igr.value) + 'bn' : '—', 'IGR'), full && s.gdp ? this.stat('fas fa-chart-line', naira(s.gdp.value, 'trn'), 'GDP') : null,
    // only once there's data
    this.stat('fas fa-crown', s.kingdoms.length, s.kingdoms.length === 1 ? 'Kingdom' : 'Kingdoms'), this.stat('fas fa-hourglass-half', s.avgAge ? '≈' + s.avgAge + ' yrs' : '—', 'Avg. age')];
  }
  emblem(s, big) {
    return m("div", {
      className: 'VKing-emblem' + (big ? ' is-big' : ''),
      "aria-hidden": "true"
    }, m("i", {
      className: "fas fa-crown"
    }), m("span", null, s.state.replace(/\s*\(.*\)/, '').slice(0, 3).toUpperCase()));
  }
  kingdoms(s) {
    return m("div", {
      className: "VKing-kingdoms"
    }, s.kingdoms.map(k => m("div", {
      className: "VKing-row"
    }, m("div", {
      className: "VKing-main"
    }, m("strong", null, k.kingdom), m("span", {
      className: "VKing-title"
    }, [k.title, k.name].filter(Boolean).join(' · ') || 'Ruler not listed')), m("div", {
      className: "VKing-meta"
    }, k.founded ? m("span", {
      title: "Founded"
    }, k.founded, k.years ? ' · ' + k.years + ' yrs' : '') : null, k.grade ? m("span", {
      className: "VKing-grade"
    }, k.grade) : null, k.source ? m("a", {
      href: k.source,
      target: "_blank",
      rel: "noopener",
      title: "Source"
    }, m("i", {
      className: "fas fa-external-link-alt",
      "aria-hidden": "true"
    })) : null))));
  }
  featured(s) {
    const open = this.open === s.slug;
    return m("div", null, m("div", {
      className: 'VKingFeat' + rankTier(s.rank, this.ranked) + (open ? ' is-open' : ''),
      onclick: () => this.toggle(s),
      role: "button",
      tabindex: "0"
    }, m("div", {
      className: "VKingFeat-head"
    }, s.rank ? m("span", {
      className: "VKing-rankPill"
    }, "#", s.rank) : null, m("div", {
      className: "VKingFeat-name"
    }, m("h2", null, s.state), s.zone ? m("span", null, s.zone) : null), m("span", {
      className: "VGov-toggle"
    }, open ? 'Hide kingdoms' : 'View kingdoms', " ", m("i", {
      className: 'fas fa-chevron-' + (open ? 'up' : 'down')
    }))), m("div", {
      className: "VKingFeat-stats"
    }, this.stats(s, true))), open ? this.kingdoms(s) : null);
  }
  row(s) {
    const open = this.open === s.slug;
    return m("div", {
      key: s.slug
    }, m("div", {
      className: 'VKingRow' + rankTier(s.rank, this.ranked) + (open ? ' is-open' : ''),
      onclick: () => this.toggle(s),
      role: "button",
      tabindex: "0"
    }, m("span", {
      className: "VKingRow-rank"
    }, "#", s.rank ?? '—'), m("div", {
      className: "VKingRow-name"
    }, m("strong", null, s.state), m("span", null, s.zone)), m("div", {
      className: "VKingRow-stats"
    }, this.stats(s, false)), m("i", {
      className: 'fas fa-chevron-' + (open ? 'up' : 'right') + ' VKingRow-chevron',
      "aria-hidden": "true"
    })), open ? this.kingdoms(s) : null);
  }
  view() {
    if (!this.states) return m(LoadingIndicator, null);
    const q = this.query.trim().toLowerCase();
    let list = this.states;
    if (q) {
      list = list.map(s => (s.state + ' ' + (s.zone || '')).toLowerCase().includes(q) ? s : {
        ...s,
        kingdoms: s.kingdoms.filter(k => [k.kingdom, k.title, k.name].join(' ').toLowerCase().includes(q))
      }).filter(s => s.kingdoms.length);
    } else if (this.chosen) {
      list = list.filter(s => s.slug === this.chosen);
    }
    const total = list.length;
    const capped = !q && !this.chosen && !this.showAll;
    if (capped) list = list.slice(0, SHOWN);
    const [first, ...rest] = list;
    const states = this.states.filter(s => s.slug !== 'abuja').length;
    const kingdomCount = this.states.reduce((n, s) => n + s.kingdoms.length, 0);
    return m("div", {
      className: "VKing"
    }, m("div", {
      className: "VKing-controls"
    }, m("div", {
      className: "VGov-search"
    }, m("i", {
      className: "fas fa-search",
      "aria-hidden": "true"
    }), m("input", {
      className: "FormControl",
      placeholder: "Search states, kingdoms, rulers or titles",
      value: this.query,
      oninput: e => this.query = e.target.value
    }), this.query ? m("button", {
      className: "VGov-clear",
      "aria-label": "Clear search",
      onclick: () => this.query = ''
    }, m("i", {
      className: "fas fa-times"
    })) : null), m("span", {
      className: "VKing-summary"
    }, kingdomCount, " kingdoms in ", states, " states", this.states.length > states ? ' and the FCT' : '')), this.chosen && !q ? m("div", {
      className: "VKing-chosen"
    }, m("button", {
      className: "Button Button--link",
      onclick: () => this.showState(null)
    }, m("i", {
      className: "fas fa-arrow-left"
    }), " All states")) : null, !first ? m("p", {
      className: "VKing-empty"
    }, this.chosen ? 'No kingdoms are listed for this state yet.' : 'Nothing matches. Try another search.') : [m("h3", {
      className: "VGov-heading"
    }, q ? 'Search result' : this.chosen ? 'Kingdoms in ' + first.state : 'Top ranked'), this.featured(first), rest.length ? m("h3", {
      className: "VGov-heading"
    }, capped ? 'The rest of the top ' + SHOWN : 'Other states') : null, rest.length ? m("div", {
      className: "VKingList"
    }, rest.map(s => this.row(s))) : null], !q && !this.chosen && total > SHOWN ? m("div", {
      className: "VGov-showMore"
    }, m("button", {
      className: "Button",
      onclick: () => this.showAll = !this.showAll
    }, this.showAll ? 'Show top ' + SHOWN : 'Show all ' + total + ' states'), !this.showAll ? m("span", null, "or search above") : null) : null, m("p", {
      className: "VKing-note"
    }, "States in the order of the governors ranking. Major kingdoms and emirates listed by naijahistory.com; not every recognised traditional ruler. Average age: from the founding years listed."));
  }
}
flarum.reg.add('voucha-community', 'forum/components/CommunityKingdoms', CommunityKingdoms);